import { Button, Form, Input } from 'antd';
import { observer } from 'mobx-react';
import { FC, useEffect } from 'react';
import { EasyField } from 'react-antd-formutil';
import { useLocation } from 'react-router-dom';
import Card, { CardButtonWrap } from 'components/Card';
import Debug from 'components/Debug';
import FormItem from 'components/FormItem';
import ScrollTop from 'components/ScrollTop';
import RadioSelect from 'modules/Transfer/components/RadioSelect';
import { TransferAssetFormData } from 'modules/Transfer/type';
import withTransferForm, { PageIdEnum, TransferFormProps } from 'modules/Transfer/withTransferForm';
import { PARTIAL_ACATS_BASE_PATH } from '../../helper';
import { PartialDetailsPageFormutil } from '../../type';
import { optionLongShortOptions, symbolLabelMap, symbolPlaceholderMap, titleMap } from '../helper';
import style from '../style.scss';
import { validateOptionSymbol } from './helper';

const FillOption: FC<TransferFormProps<TransferAssetFormData>> = observer(
    ({ $formutil, getStorageDataByKey, onSubmit, setStorageDataByKey, goNextPath }) => {
        const location = useLocation();
        const locationState = location.state as { isEdit: boolean; editOptionName: string } | undefined;
        const { isEdit, editOptionName } = locationState || {};
        // 点进添加期权页面的时候, partialDetails页面可能还没有缓存的值
        const partialDetailsData = getStorageDataByKey<PartialDetailsPageFormutil | undefined>(
            PageIdEnum.PARTIAL_DETAILS
        );
        const { temporaryOptionList } = partialDetailsData || {};
        const { $params, $setValues } = $formutil;
        const { symbol_name: formSymbol } = $params;
        const handleSymbolChange = (symbol: string) => {
            $setValues({ symbol_name: symbol });
        };
        const handleDelete = () => {
            onSubmit(() => {
                // 需要更新partial-details的temporaryOptionList
                setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, {
                    ...partialDetailsData,
                    temporaryOptionList: temporaryOptionList?.filter(item => item.symbol !== formSymbol) || []
                });

                goNextPath('../partial-details');
            });
        };

        const handleSubmit = () => {
            onSubmit(() => {
                if (isEdit && temporaryOptionList) {
                    setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, {
                        ...partialDetailsData,
                        /**
                         * 分为以下几种可能得情况:
                         * 1. 如果操作的是当前期权项, 则将编辑的期权项替换成当前表单项
                         * 2. 如果修改成了新的期权, 则将编辑的期权项替换成当前表单项
                         * 3. 这里不能改成已经存在的非当前股票, 会禁止选中
                         */
                        temporaryOptionList: temporaryOptionList.map(i => (i.symbol === editOptionName ? $params : i))
                    });
                } else {
                    // 需要更新partial-details的temporaryOptionList
                    setStorageDataByKey(PageIdEnum.PARTIAL_DETAILS, {
                        ...partialDetailsData,
                        // temporaryOptionList可能不存在, 也可能已经有值
                        temporaryOptionList: (temporaryOptionList || []).concat($params)
                    });
                }

                goNextPath('../partial-details');
            });
        };

        useEffect(() => {
            if (isEdit && temporaryOptionList) {
                const symbolData = temporaryOptionList.find(item => item.symbol === editOptionName);

                if (symbolData) {
                    $setValues(symbolData);
                }
            }
        }, [isEdit, editOptionName, temporaryOptionList, $setValues]);

        return (
            <Card form>
                <ScrollTop />
                <h3 className="transfer-title">{titleMap.option}</h3>
                <Form noValidate layout="vertical" onFinish={handleSubmit}>
                    <FormItem
                        // symbol对应股票的symbol, symbol_name是nameCN, 是为了展示
                        name="symbol"
                        label={symbolLabelMap.option}
                        $onFieldChange={handleSymbolChange}
                        // 额外的提示信息，和 help 类似，当需要错误信息和提示文案同时出现时，可以使用这个
                        extra={__('请按如下格式填写：MARA 20240503 25.0 CALL')}
                        checker={value => {
                            if (!value) {
                                return symbolPlaceholderMap.option;
                            }

                            if (!validateOptionSymbol(value)) {
                                return __('请按如下格式填写：MARA 20240503 25.0 CALL');
                            }

                            /**
                             * 如果symbol已经存在于temporaryOptionList中:
                             * 编辑模式下:
                             * 1. 如果当前修改的就是这个symbol, 则可以正常添加
                             * 2. 如果当前修改的不是这个symbol, 则提示用户
                             *
                             * 新增模式下:
                             * 1. 如果symbol已经存在于temporaryOptionList中, 则提示用户
                             */
                            if (temporaryOptionList?.find(i => i.symbol === value)) {
                                if (isEdit) {
                                    if (value === editOptionName) {
                                        return true;
                                    }

                                    return __(
                                        'This symbol has already been added. You may return to the list page to view or modify it.'
                                    );
                                }

                                return __('This symbol has already been added.');
                            }

                            return true;
                        }}>
                        <Input placeholder={symbolPlaceholderMap.option} autoComplete="off" />
                    </FormItem>
                    {/* symbol对应股票的symbol, symbol_name是nameCN, 是为了展示 */}
                    <EasyField name="symbol_name" type="hidden" />
                    {/* 这个字段是为了传给server, 用于区分期权和股票 */}
                    <EasyField name="secType" type="hidden" $defaultValue="OPTION" />
                    <RadioSelect
                        label={<div className={style.boldLabel}>{__('Long/Short')}</div>}
                        name="long_short"
                        options={optionLongShortOptions}
                        $defaultValue="long"
                    />
                    <FormItem
                        required
                        name="quantity"
                        label={__('Transfer:Quantity')}
                        className={style.numberInputItem}
                        validMessage={{ required: __('Please input quantity') }}
                        getValueFromEvent={({ target }) => target.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '')}>
                        <Input placeholder={__('Enter')} autoComplete="off" />
                    </FormItem>
                    <CardButtonWrap className="mt-5">
                        <Button size="large" onClick={isEdit ? handleDelete : () => goNextPath('../partial-details')}>
                            {isEdit ? __('Delete') : __('Back')}
                        </Button>
                        <Button type="primary" size="large" htmlType="submit">
                            {isEdit ? __('Save') : __('Add List')}
                        </Button>
                    </CardButtonWrap>
                    <Debug data={$params} />
                </Form>
            </Card>
        );
    }
);

export default withTransferForm({
    id: PageIdEnum.FILL_OPTION,
    step: 3,
    basePath: PARTIAL_ACATS_BASE_PATH,
    // 这个页面的内容只是为了操作表单, 甚至可以理解为一个弹窗, 不需要缓存表单值
    cache: false,
    /**
     * 使用PageIdEnum.BROKER/PageIdEnum.CHECK来判断是否是手敲链接的误入
     * 注意这里不能加PageIdEnum.PARTIAL_DETAILS
     * 因为首次进入的时候缓存中还没有PageIdEnum.PARTIAL_DETAILS的值
     */
    ensure: [PageIdEnum.BROKER, PageIdEnum.CHECK]
})(FillOption);
